import type { Instance } from "mobx-state-tree";
import { types, flow, getRoot, cast } from "mobx-state-tree";
import * as repo from "./repository";
import { DocumentTemplate, TemplateLink } from "./models";
import type {
  DocumentTemplateRow,
  DocumentTemplateSnapshotIn,
  TemplateLinkSnapshotIn,
} from "./models";
import type { AdminRootAccess } from "../rootAccess";
import type { Tables, TablesInsert, TablesUpdate } from "@/types/supabase-helpers";

/**
 * The "New document" template catalogue and the classification each template's documents
 * are born with — what the Templates Linking admin section edits.
 */
export const DocumentTemplatesStore = types
  .model("DocumentTemplatesStore", {
    templates: types.array(DocumentTemplate),
    links: types.array(TemplateLink),
    isLoading: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
  })
  .views((self) => ({
    get templatesList() {
      return self.templates.slice();
    },
    get linksList() {
      return self.links.slice();
    },
    /** The link for a template, or undefined when that template has none configured. */
    linkForTemplate(templateId: number) {
      return self.links.find((l) => l.template_id === templateId);
    },
  }))
  .actions((self) => ({
    loadTemplates: flow(function* loadTemplates() {
      self.isLoading = true;
      self.error = null;
      try {
        const data: DocumentTemplateRow[] = yield repo.listDocumentTemplates();
        self.templates.clear();
        data.forEach((t) => self.templates.push(t as DocumentTemplateSnapshotIn));
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      } finally {
        self.isLoading = false;
      }
    }),

    loadLinks: flow(function* loadLinks() {
      self.isLoading = true;
      self.error = null;
      try {
        const data: repo.TemplateLink[] = yield repo.listTemplateLinks();
        self.links.clear();
        data.forEach((l) => self.links.push(l as TemplateLinkSnapshotIn));
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      } finally {
        self.isLoading = false;
      }
    }),
  }))
  .actions((self) => {
    /**
     * Fold a just-written row into `links` before the reload, so a failed reload cannot
     * leave the row looking unconfigured — the admin would press Save again and hit the
     * unique constraint on template_id for a link that had actually saved.
     *
     * The write returns ids but not the joined names, so those are read from the lists the
     * table already loaded; the reload replaces this row with the authoritative one.
     */
    const mergeWrittenLink = (row: Tables<"document_template_links">) => {
      const root = getRoot<AdminRootAccess>(self);
      const template = self.templates.find((t) => t.id === row.template_id);
      const snapshot: TemplateLinkSnapshotIn = {
        id: row.id,
        template_id: row.template_id,
        template_key: template?.template_key ?? "",
        template_name: template?.name ?? "",
        category_id: row.category_id,
        category_name: root.documentCategoriesStore.getById(row.category_id)?.category_name ?? "",
        category_mode: row.category_mode,
        stage_id: row.stage_id,
        stage_name: row.stage_id
          ? (root.designStagesStore.getById(row.stage_id)?.name ?? null)
          : null,
        feed_form_values: row.feed_form_values,
      };
      const index = self.links.findIndex((l) => l.id === row.id);
      if (index === -1) self.links.push(snapshot);
      else self.links[index] = cast(snapshot);
    };

    return {
      /**
       * Create or update a template's link, then reload so the joined category and stage
       * names in the table match what was just saved. The write returns the raw row, which
       * carries ids but not names, so a reload is the honest way to refresh the view.
       *
       * A reload that fails after a successful write is NOT a failed save: it is recorded
       * on `error` and swallowed, because rethrowing it would report a saved link as
       * unsaved and invite a duplicate create.
       */
      saveLink: flow(function* saveLink(
        linkId: number | null,
        values: {
          template_id: number;
          category_id: number;
          category_mode: "skip_llm" | "additional";
          stage_id: number | null;
          feed_form_values: boolean;
        }
      ) {
        self.error = null;
        let row: Tables<"document_template_links">;
        try {
          const root = getRoot<AdminRootAccess>(self);
          const userProfileId = root.authStore.userProfileId;
          if (!userProfileId) {
            throw new Error("User must be authenticated to save template links");
          }
          if (linkId === null) {
            row = yield repo.createTemplateLink(
              values as TablesInsert<"document_template_links">,
              userProfileId
            );
          } else {
            row = yield repo.updateTemplateLink(
              linkId,
              values as TablesUpdate<"document_template_links">,
              userProfileId
            );
          }
          mergeWrittenLink(row);
        } catch (e) {
          self.error = e instanceof Error ? e.message : String(e);
          throw e;
        }

        try {
          yield self.loadLinks();
        } catch (e) {
          self.error = `The link was saved but the list could not be refreshed: ${
            e instanceof Error ? e.message : String(e)
          }`;
        }
      }),

      deleteLink: flow(function* deleteLink(linkId: number) {
        self.error = null;
        try {
          yield repo.deleteTemplateLink(linkId);
          const index = self.links.findIndex((l) => l.id === linkId);
          if (index !== -1) self.links.splice(index, 1);
        } catch (e) {
          self.error = e instanceof Error ? e.message : String(e);
          throw e;
        }
      }),

      clear() {
        self.templates.clear();
        self.links.clear();
        self.isLoading = false;
        self.error = null;
      },
    };
  });

export type DocumentTemplatesStoreInstance = Instance<typeof DocumentTemplatesStore>;
